import { debounce } from "lodash-es";
import { is } from "ramda";
import { Component, type ReactElement } from "react";
import { FocusScope } from "react-aria";
import { type InjectedFormProps, reduxForm, type Validator } from "redux-form";
import { Callout, CalloutIntent } from "widgets";
import { checkIsTextOfAFloat32, RXEXP_FUZZY_INTEGER_NUMBER } from "widgets/utils";

import { type ISelectOption } from "@/app-utils/form.ts";
import { leftPad } from "@/app-utils/string.ts";
import { type IEditable, type IEditCellChange } from "@/redux/modules/data-editing.ts";
import { type TSampleCellValue } from "@/redux/modules/sample-editing.ts";
import { ATTRIBUTE_TYPE, CELL_TYPE, EDIT_GRID_COLUMN_ACTIONS } from "@/settings/settings.ts";

import { type ICustomColDef } from "../../core/grid/DataTable.js";
import { Dialog } from "../../core/overlays/Dialogs.js";
import { ThreeStatesBooleanReduxFormField } from "../../core/redux-form/BooleanField.js";
import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import { ReactDateSelectReduxFormField } from "../../core/redux-form/ReactDateSelectorField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../../core/redux-form/ReactSelectField.js";

export interface ICopyDialogSubmitData {
  prefix: string;
  startValue: number;
  suffix: string;
  textValue: string;
  numberValue: number;
  booleanValue: boolean;
  date: any;
}

export interface ISampleEditGridCopyDialogProps extends InjectedFormProps<any, any> {
  column: ICustomColDef;
  dataGrid: IEditable[];
  copyAction: EDIT_GRID_COLUMN_ACTIONS;

  setSampleGridChanges?: (changes: IEditCellChange[]) => void;
  onClose?: () => void;
}

function isNumericCellType(type): boolean {
  switch (type) {
    case CELL_TYPE.NUMBER:
    case CELL_TYPE.INTEGER:
      return true;
  }
  return false;
}

class SampleEditGridCopyDialogImpl extends Component<ISampleEditGridCopyDialogProps> {
  public static SAMPLE_EDIT_GRID_COPY_FORMNAME = "SAMPLE_EDIT_GRID_COPY_DIALOG";
  private static ASYNC_VALIDATION_DEBOUNCE_TIMER = 300;

  public static SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_PREFIX = "copyPrefix";
  public static SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_START_VALUE = "copyStartValue";
  public static SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_SUFFIX = "copySUFFIX";
  public static SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_TEXT = "copyText";
  public static SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_NUMBER = "copyNumber";
  public static SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_DATE = "copyDate";
  public static SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_BOOLEAN = "copyBoolean";

  constructor(props: ISampleEditGridCopyDialogProps) {
    super(props);
    this.handleSubmitData = this.handleSubmitData.bind(this);
    this.debouncedNameValidation = this.debouncedNameValidation.bind(this);
  }

  initFormValues = (props: ISampleEditGridCopyDialogProps) => {
    props.change(SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_PREFIX, null);
    props.change(SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_START_VALUE, null);
    props.change(SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_SUFFIX, null);
    props.change(SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_TEXT, null);
    props.change(SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_NUMBER, null);
    props.change(SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_DATE, null);
    props.change(SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_BOOLEAN, null);
  };

  componentDidMount() {
    this.debouncedNameValidation = debounce(
      this.debouncedNameValidation,
      SampleEditGridCopyDialogImpl.ASYNC_VALIDATION_DEBOUNCE_TIMER,
    );
    this.initFormValues(this.props);
  }

  debouncedNameValidation() {
    this.props.asyncValidate();
  }

  handleSubmitData(values): void {
    const myTextInput: any = values[SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_TEXT];

    const attributeType: ATTRIBUTE_TYPE = this.props.column.attribute?.attributeType.id;

    const submitData: ICopyDialogSubmitData = {
      prefix: values[SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_PREFIX],
      startValue: values[SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_START_VALUE],
      suffix: values[SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_SUFFIX],
      textValue:
        attributeType === ATTRIBUTE_TYPE.CONTROLLED_TEXT || attributeType === ATTRIBUTE_TYPE.DYNAMIC_TERMS
          ? (myTextInput as ISelectOption).label
          : myTextInput,
      numberValue: values[SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_NUMBER],
      booleanValue: values[SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_BOOLEAN],
      date: values[SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_DATE],
    };

    this.submitCopy(submitData);

    this.props.reset();
    this.props.onClose();
  }

  submitCopy = (submitData: ICopyDialogSubmitData) => {
    const colDef: ICustomColDef = this.props.column;
    const colId = colDef.colId;

    // supporting leading zeros (for text attributes): record total number of chars in the count, then strip its leading zeros
    let count: number;
    let countChars = 0;
    if (
      (isNumericCellType(colDef.type) || colDef.type === CELL_TYPE.TEXT) &&
      submitData.startValue != undefined &&
      is(String, submitData.startValue)
    ) {
      // calculate the max number of characters we will reach for the incremental number
      count = Number(String(submitData.startValue).replace(/^0+/, ""));
      countChars = String(count + this.props.dataGrid.length - 1).length;
    }

    const cellChanges: IEditCellChange[] = this.props.dataGrid
      .filter((row) => !row.locked && colDef.attribute?.editable)
      .map((row) => {
        if (row.editing[colId].readOnly) {
          return null;
        }

        let newValue: TSampleCellValue = null;

        switch (colDef.type) {
          case CELL_TYPE.DATE:
            newValue = submitData.date;
            break;
          case CELL_TYPE.NUMBER:
          case CELL_TYPE.INTEGER:
            newValue = count != undefined ? String(count) : String(submitData.numberValue);
            break;
          case CELL_TYPE.TEXT:
            switch (colDef.attribute?.attributeType.id) {
              case ATTRIBUTE_TYPE.CONTROLLED_TEXT:
              case ATTRIBUTE_TYPE.DYNAMIC_TERMS:
                newValue = submitData.textValue;
                break;
              default:
                // padding the number with as many leading zeroes as required
                newValue =
                  count != undefined
                    ? (submitData.prefix || "") + leftPad(String(count), countChars, "0") + (submitData.suffix || "")
                    : submitData.textValue;
            }
            break;
          case CELL_TYPE.BOOLEAN:
            newValue = submitData.booleanValue; // it can be null or a real boolean value
            break;
          default:
            newValue = "";
        }

        const editCellChange: IEditCellChange = {
          rowId: row.id,
          column: { columnId: colDef.colId },
          newValue: newValue,
        };

        if (typeof count === "number" && !Number.isNaN(count)) {
          count++;
        }

        return editCellChange;
      })
      .filter((e) => !!e); // remove empty entries

    return this.props.setSampleGridChanges(cellChanges); //.then(this.props.onCancel);
  };

  inputValidator: Validator = (value: any, _allValues?: any, props?: ISampleEditGridCopyDialogProps) => {
    if (value == undefined && props.column.attribute.mandatory) {
      return "This field cannot be empty.";
    }

    const isTextAttribute: boolean = CELL_TYPE.TEXT === this.props.column.type;
    const isNumericAttribute: boolean = isNumericCellType(this.props.column.type);
    const isAutoIncrement: boolean = this.props.copyAction === EDIT_GRID_COLUMN_ACTIONS.AUTO_INCREMENT;

    // error if (numerical attribute or auto incremented text) and not a proper number
    if (isNumericAttribute || (isAutoIncrement && isTextAttribute)) {
      const notAFloat = checkIsTextOfAFloat32(value);
      if (notAFloat) {
        return notAFloat.message;
      }
    }
    // error if autoincrement (text or number) and number is not an integer or < 0
    if (
      isAutoIncrement &&
      (!RegExp("^" + RXEXP_FUZZY_INTEGER_NUMBER + "$").test(value) ||
        (isTextAttribute && Number.parseInt(value, 10) < 0))
    ) {
      if (isTextAttribute) {
        return "Invalid value, please enter an integer value greater or equal zero.";
      } else {
        return "Invalid value, please enter an integer value.";
      }
    }
  };

  render(): ReactElement {
    const column: ICustomColDef = this.props.column;

    const isAutoCopy: boolean = this.props.copyAction === EDIT_GRID_COLUMN_ACTIONS.AUTO_COPY;
    const isAutoIncrement: boolean = this.props.copyAction === EDIT_GRID_COLUMN_ACTIONS.AUTO_INCREMENT;

    let label: string = null;

    if (isAutoIncrement) {
      label = "Auto Increment: ";
    } else {
      label = "Auto Copy: ";
    }

    const fullLabel = column ? `${label}${column.headerName}` : null;

    const attributeType: ATTRIBUTE_TYPE = this.props.column.attribute?.attributeType.id;

    return (
      <Dialog
        headerProps={{ title: fullLabel }}
        mainActionButtonProps={{
          children: "OK",
          isDisabled: false,
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        closeFn={() => {
          this.props.reset();
          this.props.onClose();
        }}
      >
        <FocusScope contain restoreFocus autoFocus>
          <div style={{ marginBottom: "10px" }}>
            {isAutoIncrement
              ? "Values for all samples will be assigned by incrementing from top to bottom with the number below."
              : "The value entered below will be copied for all rows."}
          </div>

          {isAutoIncrement && !isNumericCellType(this.props.column.type) ? (
            <InputReduxFormField
              componentClass="input"
              name={SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_PREFIX}
              placeholder="Optional Prefix"
              maxLength={100}
              component={InputField}
              handleChange={this.debouncedNameValidation}
            />
          ) : null}

          {isAutoIncrement ? (
            <InputReduxFormField
              componentClass="input"
              name={SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_START_VALUE}
              placeholder="Enter a Number"
              type={"number"}
              maxLength={100}
              component={InputField}
              validate={this.inputValidator}
            />
          ) : null}

          {isAutoIncrement && !isNumericCellType(this.props.column.type) ? (
            <InputReduxFormField
              componentClass="input"
              name={SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_SUFFIX}
              placeholder="Optional Suffix"
              maxLength={100}
              component={InputField}
            />
          ) : null}

          {isAutoCopy && isNumericCellType(this.props.column.type) ? (
            <InputReduxFormField
              componentClass="input"
              name={SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_NUMBER}
              placeholder={"Enter a Number"}
              maxLength={100}
              type={"number"}
              component={InputField}
              validate={this.inputValidator}
            />
          ) : null}

          {isAutoCopy && (attributeType === ATTRIBUTE_TYPE.FREE_TEXT || attributeType === ATTRIBUTE_TYPE.HYPERLINK) ? (
            <InputReduxFormField
              componentClass="input"
              name={SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_TEXT}
              placeholder={"Enter a Text"}
              maxLength={100}
              component={InputField}
              validate={this.inputValidator}
            />
          ) : null}

          {isAutoCopy &&
          (attributeType === ATTRIBUTE_TYPE.CONTROLLED_TEXT || attributeType === ATTRIBUTE_TYPE.DYNAMIC_TERMS) ? (
            <ReactSelectReduxFormField
              name={SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_TEXT}
              placeholder="Please select..."
              options={this.props.column.cellEditorParams.selectOptions}
              component={ReactSelectField}
              validate={this.inputValidator}
              isClearable={false}
              isSearchable={true}
            />
          ) : null}

          {isAutoCopy && this.props.column.type === CELL_TYPE.DATE ? (
            <ReactDateSelectReduxFormField
              name={SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_DATE}
              placeholder="Select a date"
              withTime={false}
              validate={this.inputValidator}
            />
          ) : null}

          {isAutoCopy && this.props.column.type === CELL_TYPE.BOOLEAN ? (
            <ThreeStatesBooleanReduxFormField
              name={SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_DIALOG_FIELD_BOOLEAN}
              validate={this.inputValidator}
            />
          ) : null}

          {this.props.error && <Callout intent={CalloutIntent.Danger}>{this.props.error}</Callout>}
        </FocusScope>
      </Dialog>
    );
  }
}

const SampleEditGridCopyDialog = reduxForm<{}, ISampleEditGridCopyDialogProps>({
  form: SampleEditGridCopyDialogImpl.SAMPLE_EDIT_GRID_COPY_FORMNAME,
  //asyncValidate:   SampleEditGridCopyDialogImpl.asyncValidate,
  //asyncBlurFields: [SampleEditGridCopyDialogImpl.USER_GROUP_DIALOG_FIELD_NAME], // so that only blurring this field triggers the async validation
})(SampleEditGridCopyDialogImpl);

export default SampleEditGridCopyDialog;
